import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { X, Send, Mic, UserCheck, MessageSquare, ShieldCheck, CheckCircle } from 'lucide-react';
import { authFetch } from '../../utils/apiClient';

// =========================================================
// 1. BROADCAST MESSAGE MODAL (With Success Confirmation)
// =========================================================
const BroadcastModal = ({ isOpen, onClose }) => {
    // 1. State
    const [target, setTarget] = React.useState('All Active Agents (Floor)');
    const [message, setMessage] = React.useState('');
    const [isSending, setIsSending] = React.useState(false);
    const [isSuccess, setIsSuccess] = React.useState(false); // <--- New State

    // Reset state when modal opens/closes
    React.useEffect(() => {
        if (isOpen) {
            setIsSuccess(false);
            setIsSending(false);
            setMessage('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // 2. Handle Send Logic
    const handleSend = () => {
        if (!message.trim()) return;

        setIsSending(true);

        // A. Payload for Developers
        const broadcastPayload = {
            type: 'URGENT_ALERT',
            targetGroup: target,
            content: message,
            timestamp: new Date().toISOString()
        };
        console.log("🚀 BROADCAST TRANSMITTED:", broadcastPayload);

        // B. Simulate Server Response (1 second delay)
        setTimeout(() => {
            setIsSending(false);
            setIsSuccess(true); // <--- Show Success Screen

            // C. Auto-Close after 2 seconds
            setTimeout(() => {
                onClose();
            }, 2000);
        }, 1000);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200 overflow-hidden">
                
                {/* CONDITIONAL RENDERING: FORM vs SUCCESS */}
                {!isSuccess ? (
                    <>
                        {/* --- VIEW 1: THE FORM --- */}
                        <div className="flex justify-between items-center p-4 border-b border-slate-700">
                            <h3 className="text-white font-bold flex items-center gap-2">
                                <Send size={18} className="text-blue-400" /> Broadcast Message
                            </h3>
                            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
                        </div>
                        
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 mb-1">Target Audience</label>
                                <select 
                                    value={target}
                                    onChange={(e) => setTarget(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                >
                                    <option>All Active Agents (Floor)</option>
                                    <option>Sales Team Only</option>
                                    <option>Support Team Only</option>
                                    <option>Compliance Officers</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 mb-1">Message</label>
                                <textarea 
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 h-24 resize-none"
                                    placeholder="Type your alert here..."
                                ></textarea>
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
                            <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
                            <button 
                                onClick={handleSend} 
                                disabled={!message.trim() || isSending}
                                className={`px-4 py-2 text-xs font-bold text-white rounded-lg transition-all flex items-center gap-2 ${
                                    isSending ? 'bg-blue-700 cursor-wait' : 'bg-blue-600 hover:bg-blue-500'
                                }`}
                            >
                                {isSending ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={14} /> Send Broadcast
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    /* --- VIEW 2: THE SUCCESS CONFIRMATION --- */
                    <div className="p-8 flex flex-col items-center justify-center text-center animate-in zoom-in duration-300">
                        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle size={32} className="text-green-500 animate-bounce" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Broadcast Sent!</h3>
                        <p className="text-sm text-slate-400">Your message has been pushed to {target}.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 2. REVIEW MODAL
// =========================================================
const ReviewModal = ({ isOpen, onClose, data }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-6 border-b border-slate-700 bg-slate-800/50 rounded-t-2xl">
                    <h3 className="text-white font-bold flex items-center gap-2">
                        <UserCheck size={20} className="text-purple-400" /> Performance Review: {data?.name}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl">
                            <h4 className="text-indigo-300 text-xs font-bold uppercase mb-2 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></span> AI Insight
                            </h4>
                            <p className="text-sm text-indigo-100 leading-relaxed">
                                {data?.name} has shown strong resilience this month. Ticket resolution speed is top 10%, but CSAT scores dipped on Tuesdays.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-400 mb-1">Manager Notes</label>
                            <textarea className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-purple-500 h-32 resize-none" placeholder="Add your observations..."></textarea>
                        </div>
                        <button className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-lg transition-colors text-sm">
                            Submit Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 3. LIVE MONITORING MODAL (Interactive Whisper Edition)
// =========================================================
const LiveMonitoringModal = ({ isOpen, onClose, data }) => {
    // 1. Setup Local State for the Chat so we can add to it
    const [messages, setMessages] = React.useState([]);
    const [inputValue, setInputValue] = React.useState("");
    const chatEndRef = React.useRef(null);

    // 2. Initialize Data when modal opens
    React.useEffect(() => {
        if (isOpen && data) {
            setMessages(data.liveChatHistory || []);
        }
    }, [isOpen, data]);

    // 3. Auto-scroll to bottom when messages change
    React.useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    if (!isOpen) return null;

    const currentTicket = data?.activeTicket || { id: 'Unknown', type: 'General' };
    const clientInfo = data?.activeClient || { name: 'Unknown Client', tier: 'Standard' };

    // 4. FUNCTION: Handle Sending a Whisper
    const handleSendWhisper = () => {
        if (!inputValue.trim()) return;

        const newMsg = { 
            sender: 'manager', 
            text: inputValue, 
            timestamp: 'Just Now', 
            type: 'whisper' // <--- Key flag for styling
        };

        // Add Manager's Whisper to UI immediately (Optimistic Update)
        setMessages((prev) => [...prev, newMsg]);
        setInputValue("");

        // NOTE: Real API call to send whisper would go here.
        // Simulation removed to prevent developer confusion.
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col h-[600px]">
                
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800/50 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-white">
                                {data?.name?.charAt(0) || 'A'}
                            </div>
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full animate-pulse"></span>
                        </div>
                        <div>
                            <h3 className="text-white font-bold flex items-center gap-2">
                                Monitoring: {data?.name}
                            </h3>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                                <span className="text-green-400">● Live</span> • Ticket #{currentTicket.id}
                            </p>
                        </div>
                    </div>
                <div className="flex gap-2">
                        {/* Barge In Button Removed */}
                        <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-full text-slate-400 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex overflow-hidden">
                    
                    {/* Left: Context Panel */}
                    <div className="hidden md:block w-1/3 border-r border-slate-700 p-4 bg-slate-800/30">
                        <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">Active Context</h4>
                        <div className="space-y-3">
                            <div className="bg-slate-800 p-3 rounded border border-slate-700">
                                <p className="text-[10px] text-slate-400">Client</p>
                                <p className="text-sm font-bold text-white">{clientInfo.name}</p>
                                <p className="text-[10px] text-blue-400 mt-1">{clientInfo.tier}</p>
                            </div>
                            <div className="bg-slate-800 p-3 rounded border border-slate-700">
                                <p className="text-[10px] text-slate-400">Current Task</p>
                                <p className="text-xs font-medium text-white">{currentTicket.type}</p>
                                <p className="text-[10px] text-slate-500 mt-1">Status: Open</p>
                            </div>
                        </div>
                    </div>

                    {/* Right: The Live Chat Interface */}
                    <div className="w-full md:w-2/3 flex flex-col bg-slate-950">
                        
                        {/* Chat Messages Area */}
                        <div className="flex-1 p-4 space-y-4 overflow-y-auto custom-scrollbar">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => {
                                    // LOGIC: Determine Bubble Style based on Sender
                                    const isAgent = msg.sender === 'agent';
                                    const isWhisper = msg.type === 'whisper';
                                    const isInternalReply = msg.type === 'internal_reply';

                                    // Managers (Whispers) align right, Agents/Clients align left
                                    const alignRight = isWhisper || (isAgent && !isInternalReply);
                                    
                                    // COLORS:
                                    let bubbleClass = "bg-slate-800 text-slate-200 rounded-tl-none"; // Default Client
                                    
                                    if (isWhisper) bubbleClass = "bg-amber-600/20 border border-amber-600/50 text-amber-200 rounded-tr-none";
                                    else if (isInternalReply) bubbleClass = "bg-amber-900/10 border border-amber-600/30 text-amber-400 rounded-tl-none italic";
                                    else if (isAgent) bubbleClass = "bg-blue-600 text-white rounded-tr-none";

                                    return (
                                        <div key={idx} className={`flex flex-col ${alignRight ? 'items-end' : 'items-start'}`}>
                                            
                                            {/* Label for Whisper/Internal to make it clear */}
                                            {(isWhisper || isInternalReply) && (
                                                <span className="text-[9px] font-bold text-amber-500 mb-1 uppercase tracking-wider flex items-center gap-1">
                                                    {isWhisper ? <Mic size={10} /> : null} 
                                                    {isWhisper ? 'Internal Whisper (Hidden from Client)' : 'Internal Reply'}
                                                </span>
                                            )}

                                            <div className={`max-w-[85%] p-3 rounded-xl text-sm ${bubbleClass}`}>
                                                <p>{msg.text}</p>
                                            </div>
                                            <p className="text-[9px] text-slate-500 mt-1">{msg.timestamp}</p>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex items-center justify-center h-full text-slate-500 text-xs">Waiting for live feed...</div>
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Whisper Input Bar */}
                        <div className={`p-3 border-t border-slate-800 bg-slate-900 ${data?.initialMode === 'whisper' ? 'ring-1 ring-amber-500/50' : ''}`}>
                            <form 
                                onSubmit={(e) => { e.preventDefault(); handleSendWhisper(); }}
                                className="flex gap-2"
                            >
                                <input 
                                    type="text" 
                                    autoFocus={data?.initialMode === 'whisper'}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={`Whisper to ${data?.name}... (Client won't see this)`} 
                                    className="flex-1 bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 focus:outline-none transition-all"
                                />
                                <button 
                                    type="submit"
                                    className="p-2.5 bg-amber-600 hover:bg-amber-500 rounded-lg text-white transition-colors shadow-lg shadow-amber-900/20"
                                >
                                    <Mic size={18} />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 4. TICKET WORKSPACE MODAL
// =========================================================
const TicketWorkspaceModal = ({ isOpen, onClose, data }) => {
    const [reply, setReply] = useState('');
    const navigate = useNavigate(); 

    if (!isOpen) return null;

    const handleGoToClient = () => {
        onClose();
        // Redirect to client page, switching to 'tickets' tab
        navigate(`/client/${data?.clientId || '1'}`, { state: { activeTab: 'tickets' } });
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col h-[600px]">
                <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800/50 rounded-t-2xl">
                    <div>
                        <h3 className="text-white font-bold flex items-center gap-2">
                            <MessageSquare size={18} className="text-purple-400" /> Ticket #{data?.id || '000'}
                        </h3>
                        <p className="text-xs text-slate-400">Client: <span className="text-white font-bold">{data?.client}</span> • {data?.subject}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-full text-slate-400 hover:text-white"><X size={20} /></button>
                </div>
                <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-slate-950">
                    <div className="flex flex-col items-start">
                        <div className="bg-slate-800 p-3 rounded-xl rounded-tl-none text-sm text-slate-300 max-w-[80%]">
                            <p>Hi, I am having trouble with the withdrawal I requested yesterday. It still says pending.</p>
                        </div>
                        <span className="text-[10px] text-slate-500 mt-1 ml-1">{data?.time} ago</span>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className="bg-blue-900/20 border border-blue-500/30 p-3 rounded-xl rounded-tr-none text-sm text-blue-200 max-w-[80%]">
                            <p>Hello {data?.client}, I'm looking into this for you right now.</p>
                        </div>
                        <span className="text-[10px] text-slate-500 mt-1 mr-1">Automated Msg</span>
                    </div>
                </div>
                <div className="p-4 border-t border-slate-700 bg-slate-900">
                    <textarea 
                        value={reply} 
                        onChange={(e) => setReply(e.target.value)} 
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-purple-500 h-24 resize-none mb-3" 
                        placeholder="Type your reply here..."
                    ></textarea>
                    <div className="flex justify-between items-center">
                        <button onClick={handleGoToClient} className="text-xs text-slate-500 hover:text-white underline">View Full Client Profile ↗</button>
                        <div className="flex gap-2">
                            <button onClick={onClose} className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white">Cancel</button>
                            <button onClick={() => { alert('Reply Sent!'); onClose(); }} className="px-4 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors flex items-center gap-2"><Send size={14} /> Send Reply</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 5. KYC REVIEW MODAL (Fully Wired to Backend)
// =========================================================
const KYCReviewModal = ({ isOpen, onClose, data }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    if (!isOpen) return null;

    // The function to handle Approving or Rejecting
    const handleKycAction = async (statusAction) => {
        setIsProcessing(true);
        try {
            const BACKEND_URL = process.env.REACT_APP_NOMMIA_API_URL || 'https://nommia-crm-backend-production.up.railway.app';
            const response = await authFetch(`${BACKEND_URL}/api/kyc/update`, {
                method: 'POST',
                body: JSON.stringify({
                    id: data?.id, // The internal Nommia ID (e.g., 100)
                    status: statusAction, // 'accepted' or 'rejected'
                    reject_reason: statusAction === 'rejected' ? 'Manual review failure' : ''
                })
            });

            const result = await response.json();
            
            if (result.status) {
                alert(`Success! KYC ${statusAction.toUpperCase()}`);
                onClose(); // Close the modal on success
            } else {
                alert(`Failed to update: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error("KYC Action Error:", error);
            alert("Error communicating with the backend.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl shadow-2xl animate-in fade-in zoom-in duration-200 flex flex-col h-[700px]">
                
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800/50 rounded-t-2xl">
                    <div>
                        <h3 className="text-white font-bold flex items-center gap-2">
                            <ShieldCheck size={18} className="text-purple-400" /> KYC Review
                        </h3>
                        <p className="text-xs text-slate-400">Client Email: <span className="text-white font-bold">{data?.email}</span></p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-full text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
                </div>
                
                {/* Body */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Side: Document Viewer (Simulated for now, would connect to an iDenfy image URL later) */}
                    <div className="w-2/3 bg-black flex items-center justify-center border-r border-slate-700 relative">
                        <div className="text-center">
                            <div className="w-48 h-64 border-2 border-slate-600 border-dashed rounded-lg flex items-center justify-center mx-auto mb-4">
                                <span className="text-slate-500 text-xs">Secure Document Render</span>
                            </div>
                            <p className="text-slate-400 text-xs">Awaiting iDenfy Image Hook</p>
                        </div>
                    </div>
                    
                    {/* Right Side: Data & Actions */}
                    <div className="w-1/3 bg-slate-900 p-6 flex flex-col">
                        <h4 className="text-xs font-bold text-slate-500 uppercase mb-4">Extracted Data</h4>
                        
                        <div className="space-y-4 mb-6 flex-1">
                            <div>
                                <label className="text-[10px] text-slate-500 block">System Name</label>
                                <p className="text-sm font-bold text-white">{data?.name}</p>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 block">Nommia ID</label>
                                <p className="text-sm font-mono text-slate-300">{data?.id}</p>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 block">Current Status</label>
                                <span className="text-xs font-bold text-amber-400 uppercase">{data?.statusText}</span>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="space-y-3 pt-6 border-t border-slate-800">
                            <button 
                                onClick={() => handleKycAction('accepted')} 
                                disabled={isProcessing}
                                className="w-full py-3 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 text-white font-bold rounded-lg transition-colors text-sm flex justify-center items-center gap-2"
                            >
                                {isProcessing ? 'Processing...' : 'Approve Document'}
                            </button>
                            <button 
                                onClick={() => handleKycAction('rejected')} 
                                disabled={isProcessing}
                                className="w-full py-3 bg-red-600/10 hover:bg-red-600 border border-red-600/50 disabled:border-slate-700 text-red-500 hover:text-white disabled:text-slate-500 font-bold rounded-lg transition-colors text-sm"
                            >
                                Reject Document
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 5. MAIN EXPORT
export const DashboardModals = ({ activeModal, onClose, modalData }) => {
return (
        <>
            <BroadcastModal isOpen={activeModal === 'broadcast'} onClose={onClose} onSubmit={() => { alert("Sent!"); onClose(); }} />
            <ReviewModal isOpen={activeModal === 'review'} onClose={onClose} data={modalData} />
            <LiveMonitoringModal isOpen={activeModal === 'monitor'} onClose={onClose} data={modalData} />
            
            {/* --- ADDED THESE 2 LINES --- */}
            <TicketWorkspaceModal isOpen={activeModal === 'ticket'} onClose={onClose} data={modalData} />
            <KYCReviewModal isOpen={activeModal === 'kyc'} onClose={onClose} data={modalData} />
        </>
    );
};